import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { CreateReasonVariablesSchema } from './use-create-reason';
import { ReasonSchema } from './use-load-reasons';

const UpdateReasonVariablesSchema = z.object({
  id: z.union([z.string(), z.number()]),
  data: CreateReasonVariablesSchema.partial(),
});

export type UpdateReasonVariables = z.infer<typeof UpdateReasonVariablesSchema>;

export async function updateReasonFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateReasonVariables,
) {
  const parsed = UpdateReasonVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  const { data } = await instance.put(`/reasons/${parsed.id}`, parsed.data);
  return ReasonSchema.parse(data);
}

export function useUpdateReason() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<z.infer<typeof ReasonSchema>, ApiError, UpdateReasonVariables>({
    mutationFn: variables => updateReasonFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['reasons'] });
    },
  });

  return addActionNameToMutationResult<
    z.infer<typeof ReasonSchema>,
    ApiError,
    UpdateReasonVariables,
    'updateReason'
  >('updateReason', mutation);
}
